import { Box, Button, useTheme } from "@mui/material";
import type { QueryKey } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { SlidersHorizontal } from "lucide-react";
import { useEffect, useMemo } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { NotificationCenterKeys } from "web_myplann/Api/NotificationCenter/notificationCenterKeys";
import { useNotificationCenterRightOptional } from "web_myplann/Api/Rights/RightsProvider";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { isTimelineFilterDefaultAtom } from "web_myplann/NotificationCenter/Atoms/timelineFilterAtom";
import { NotificationCenterTabs } from "web_myplann/NotificationCenter/Components/NotificationCenterTabs";
import { RecentTab } from "web_myplann/NotificationCenter/Components/RecentTab";
import { UpcomingTab } from "web_myplann/NotificationCenter/Components/UpcomingTab";
import {
	DEFAULT_NOTIFICATION_CENTER_TAB,
	isNotificationCenterTab,
	NOTIFICATION_CENTER_TAB_ORDER,
	NOTIFICATION_CENTER_TAB_QUERY_PARAM,
	NOTIFICATION_CENTER_TABS,
	type NotificationCenterTab,
} from "web_myplann/NotificationCenter/Constants/notificationCenterTabs";
import { NativePullToRefresh } from "web_myplann/Shared/Components/NativePullToRefresh";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import NavigationHeader from "web_myplann/Shared/Header/NavigationHeader";
import { rightsState } from "web_myplann/Shared/State/rightsAtom";

const NOTIFICATION_CENTER_QUERY_KEYS: QueryKey[] = [NotificationCenterKeys.all];

const tabIndex = (tab: NotificationCenterTab): number =>
	NOTIFICATION_CENTER_TAB_ORDER.indexOf(tab);

const tabFromIndex = (index: number): NotificationCenterTab =>
	NOTIFICATION_CENTER_TAB_ORDER[index] ?? DEFAULT_NOTIFICATION_CENTER_TAB;

export const NotificationCenterPage = () => {
	const { t } = useResource();
	const theme = useTheme();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const isFilterDefault = useAtomValue(isTimelineFilterDefaultAtom);
	// Defenziva pro přímé routování; primární gate je NotificationsRouteSwitch.
	const isRightsPending = useAtomValue(rightsState) === undefined;
	const hasNotificationCenterRight = useNotificationCenterRightOptional();

	const urlTab = searchParams.get(NOTIFICATION_CENTER_TAB_QUERY_PARAM);
	const activeTab: NotificationCenterTab = isNotificationCenterTab(urlTab)
		? urlTab
		: DEFAULT_NOTIFICATION_CENTER_TAB;

	useEffect(() => {
		if (isRightsPending || !hasNotificationCenterRight) {
			return;
		}
		if (!isNotificationCenterTab(urlTab)) {
			const next = new URLSearchParams(searchParams);
			next.set(
				NOTIFICATION_CENTER_TAB_QUERY_PARAM,
				DEFAULT_NOTIFICATION_CENTER_TAB,
			);
			setSearchParams(next, { replace: true });
		}
	}, [
		urlTab,
		searchParams,
		setSearchParams,
		isRightsPending,
		hasNotificationCenterRight,
	]);

	const tabs = useMemo(
		() => [
			{
				id: NOTIFICATION_CENTER_TABS.Upcoming,
				label: t(Resources.NotificationCenter.Tabs.Upcoming),
			},
			{
				id: NOTIFICATION_CENTER_TABS.Recent,
				label: t(Resources.NotificationCenter.Tabs.Recent),
			},
		],
		[t],
	);

	const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
		const nextTab = tabFromIndex(newValue);
		const next = new URLSearchParams(searchParams);
		next.set(NOTIFICATION_CENTER_TAB_QUERY_PARAM, nextTab);
		// Tab switches must not create history entries — the back arrow should
		// leave the notification center, not walk through previously open tabs.
		setSearchParams(next, { replace: true });
	};

	const handleFilterClick = () => {
		navigate(
			`${Routing.NotificationsFilter.path()}?${NOTIFICATION_CENTER_TAB_QUERY_PARAM}=${activeTab}`,
		);
	};

	const filterButtonColor = isFilterDefault
		? theme.palette.grey[600]
		: theme.palette.primary.main;
	const filterButtonAriaLabel = t(Resources.NotificationCenter.Filter.Button);

	if (isRightsPending) {
		return null;
	}

	if (!hasNotificationCenterRight) {
		return <Navigate to={Routing.Dashboard.path()} replace />;
	}

	return (
		<MyPlannLayout
			disableContentPadding
			topBar={
				<NavigationHeader
					title={t(Resources.NotificationCenter.Title)}
					defaultRoute={Routing.Dashboard.path()}
					endComponent={
						<Button
							variant="text"
							onClick={handleFilterClick}
							aria-label={filterButtonAriaLabel}
							data-testid="notification-center-filter-button"
							startIcon={
								<SlidersHorizontal size={18} color={filterButtonColor} />
							}
							sx={{
								color: filterButtonColor,
								fontWeight: 500,
								fontSize: 14,
								textTransform: "none",
								minHeight: 44,
								paddingInline: 0.8,
								maxWidth: "100%",
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis",
								"& .MuiButton-startIcon": { marginRight: 0.5 },
							}}
						/>
					}
					bottomComponent={
						<NotificationCenterTabs
							value={tabIndex(activeTab)}
							onChange={handleTabChange}
							tabs={tabs}
						/>
					}
					disableBottomPadding
				/>
			}
		>
			<NativePullToRefresh
				spinnerOffset={-25}
				queryKeys={NOTIFICATION_CENTER_QUERY_KEYS}
			>
				<Box pt={1.6} data-testid="notification-center-page">
					{activeTab === NOTIFICATION_CENTER_TABS.Upcoming ? (
						<UpcomingTab />
					) : (
						<RecentTab />
					)}
				</Box>
			</NativePullToRefresh>
		</MyPlannLayout>
	);
};
